import ButtonBase from '@mui/material/ButtonBase';
import { JE_TABLE_CLASSES } from '@/components/Tables/grid/StyledComponents';

import { styled, alpha, lighten } from '@mui/material/styles';
import { amber, indigo } from '@mui/material/colors';

// Cells that draw a border of their own, rather than relying on the shared column separator.
const BORDERED_CELLS = [JE_TABLE_CLASSES.CELL_MISMATCH, JE_TABLE_CLASSES.CELL_MANDATORY, JE_TABLE_CLASSES.CELL_ACTIVE];

// Every cell draws the column separator as a `::before` on its own left edge, so a bordered cell
// has to hide two of them: its own, which would sit against its left border, and the next cell's,
// which lands exactly on top of its right one.
const HIDDEN_SEPARATORS = BORDERED_CELLS.flatMap((cell) => [
  `& .${cell}:before`,
  `& .${cell} + .${JE_TABLE_CLASSES.CELL}:before`,
]).join(', ');

// Two bordered cells side by side show a 2px seam: the table does not collapse borders, so each
// draws its own 1px inside its own box. Left as is on purpose. Sibling selectors reach only within
// a row, so the horizontal seam could be closed and the vertical one could not — and a grid that is
// 1px across and 2px down reads worse than one that is 2px throughout. `border-collapse: collapse`
// closes both, but it was tried and it drops the column separators this grid draws as `::before`
// overlays, well outside anything two-way matching should be repairing.

export const JELinesTableWrapper = styled('div', { label: 'JELinesTableWrapper' })(() => ({
  position: 'relative',
  display: 'flex',
  width: '100%',
  marginBottom: 30,
  marginTop: 5,
}));

export const JELinesTableOverride = styled('div', { label: 'JELinesTableOverride' })(({ theme }) => ({
  width: '100%',
  display: 'flex',

  [`& .${JE_TABLE_CLASSES.COLGROUP_COL}`]: {
    width: 170,
  },

  [`& .${JE_TABLE_CLASSES.COLGROUP_COL_REMOVE}`]: {
    width: 30,
  },

  [`& .${JE_TABLE_CLASSES.ROW}`]: {
    '&:first-child': {
      [`& .${JE_TABLE_CLASSES.CELL}`]: {
        backgroundColor: theme.palette.grey[200],
      },
    },
    [`&:not(:first-child) .${JE_TABLE_CLASSES.CELL}`]: {
      '&:hover': {
        backgroundColor: theme.palette.grey[100],
      },
    },
  },

  [`& .${JE_TABLE_CLASSES.ROW_LINE}`]: {
    [`&.${JE_TABLE_CLASSES.ROW_ACTIVE}`]: {
      backgroundColor: indigo[50],

      [`& .${JE_TABLE_CLASSES.CELL_REMOVE}`]: {
        opacity: 1,
      },
    },

    [`&.${JE_TABLE_CLASSES.ROW_HOVERED}`]: {
      backgroundColor: amber[50],

      [`& .${JE_TABLE_CLASSES.CELL_REMOVE}`]: {
        opacity: 1,
      },
    },
  },

  [`& .${JE_TABLE_CLASSES.CELL}`]: {
    borderBottom: `1px solid ${theme.palette.grey[200]}`,
    minWidth: 170,
    width: '50%',

    '&::before': {
      content: '""',
      position: 'absolute',
      top: -1,
      left: -1,
      bottom: 2,
      borderRight: `1px solid ${theme.palette.grey[200]}`,
      zIndex: 0,
    },
  },

  [HIDDEN_SEPARATORS]: {
    display: 'none',
  },

  [`& .${JE_TABLE_CLASSES.CELL_REMOVE}`]: {
    padding: 4,
    opacity: 0,
    transition: 'opacity 0.3s',
  },

  // The document disagrees with this cell. Both values are measured off the design: the fill is
  // the partial-match row's own red at twice the row's opacity, and the border is that same red
  // at full strength — the one the unfilled mandatory cell also uses. The fill is opaque rather
  // than an alpha so it does not composite with the row underneath and land darker than drawn.
  [`& .${JE_TABLE_CLASSES.CELL_MISMATCH}`]: {
    backgroundColor: lighten(theme.palette.error.main, 0.76),
    border: `1px solid ${theme.palette.error.main}`,
  },

  [`& .${JE_TABLE_CLASSES.CELL_MANDATORY}`]: {
    border: `1px solid ${theme.palette.error.main}`,
  },

  [`& .${JE_TABLE_CLASSES.CELL_ACTIVE}`]: {
    border: `1px solid ${theme.palette.primary.light}`,
  },

  [`& .${JE_TABLE_CLASSES.CELL_PINNED}:nth-child(2):before`]: {
    display: 'none',
  },
}));

export const PinnedContainer = styled('div', { label: 'PinnedContainer' })(({ theme }) => ({
  flex: '1 1',
  position: 'relative',
  maxWidth: '70%',

  '&:after': {
    zIndex: 8,
    content: '""',
    position: 'absolute',
    display: 'block',

    right: 0,
    top: 0,
    bottom: 9,
    width: 3,
    backgroundColor: theme.palette.grey[200],
    borderLeft: `1px solid ${theme.palette.grey[300]}`,
  },

  [`& .${JE_TABLE_CLASSES.TABLE}`]: {
    position: 'relative',
    zIndex: 10, // higher than dropdown toggle
  },
}));

export const PinnedScrolledContainer = styled('div', { label: 'PinnedScrolledContainer' })(({ theme }) => ({
  width: 'auto',
  overflow: 'hidden',
  overflowX: 'auto',
  paddingTop: 0,
  paddingBottom: 2,
  paddingLeft: 3,
  paddingRight: 3,

  '&::-webkit-scrollbar': {
    appearance: 'none',
    height: 7,
  },
  '&::-webkit-scrollbar-thumb': {
    borderRadius: 4,
    backgroundColor: alpha(theme.palette.common.black, 0.5),
    boxShadow: `0 0 1px ${alpha(theme.palette.common.white, 0.5)}`,
  },
}));

export const FloatContainer = styled('div', { label: 'FloatContainer' })(() => ({
  position: 'relative',
  maxWidth: '100%',
  minWidth: 0,

  '&:hover > .MuiButtonBase-root': {
    opacity: 1,
  },
}));

export const FloatContent = styled('div', { label: 'FloatContent' })(({ theme }) => ({
  overflowX: 'auto',
  overflowY: 'hidden',
  position: 'relative',
  paddingBottom: 2,

  '&::-webkit-scrollbar': {
    appearance: 'none',
    height: 7,
  },
  '&::-webkit-scrollbar-thumb': {
    borderRadius: 4,
    backgroundColor: alpha(theme.palette.common.black, 0.5),
    boxShadow: `0 0 1px ${alpha(theme.palette.common.white, 0.5)}`,
  },
}));

export const FloatContainerAction = styled(ButtonBase, {
  label: 'FloatContainerAction',
  shouldForwardProp: (propName) => propName !== 'type',
})(({ theme, type }) => ({
  position: 'absolute',
  width: 16,
  top: 0,
  bottom: 9,
  zIndex: 8,
  outline: '0 none',
  cursor: 'pointer',
  opacity: 0,
  transition: '0.3s opacity',
  color: theme.palette.common.white,
  background: alpha(theme.palette.common.black, 0.25),

  ...(type === 'prev' && { left: 0 }),
  ...(type === 'next' && { right: 0 }),

  '&:hover': {
    zIndex: 10,
    background: alpha(theme.palette.common.black, 0.45),
  },
  '&[disabled]': {
    display: 'none',
  },
}));
